import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n/useI18n';
import KaraokrLogo from '../components/KaraokrLogo';

// Nome do evento que os bridges (tv/webos/webos.js, tv/firetv/firetv.js)
// disparam quando o botão Back é premido na landing — em vez de fecharem a
// app diretamente, pedem à app para mostrar a confirmação de saída.
export const TV_EXIT_REQUEST_EVENT = 'karaokr:exit-request';

let closing = false;
function closeApp() {
  // Idempotente: Enter no botão dispara o handler de teclado E o onClick nativo.
  if (closing) return;
  closing = true;
  window.close();
  // Fallback para plataformas que ignoram window.close() chamado por script.
  window.setTimeout(() => {
    window.location.href = 'about:blank';
  }, 400);
}

export default function ExitConfirm() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onExitRequest = () => {
      setFocusIndex(0);
      setOpen(true);
    };
    window.addEventListener(TV_EXIT_REQUEST_EVENT, onExitRequest);
    return () => window.removeEventListener(TV_EXIT_REQUEST_EVENT, onExitRequest);
  }, []);

  // Sinal para os bridges (tv/webos/webos.js, tv/firetv/firetv.js): enquanto a
  // confirmação está aberta, Back cancela em vez de re-pedir a saída. Sem isto,
  // a ordem de registo dos listeners decide se o overlay fecha ou reabre.
  useEffect(() => {
    (window as unknown as { __karaokrExitOpen?: boolean }).__karaokrExitOpen = open;
  }, [open]);

  // Teclado/telecomando com listener ao nível do documento: em TVs o foco pode
  // não estar dentro do dialog, por isso confiar no bubbling do React é frágil.
  useEffect(() => {
    if (!open) return;

    const target = focusIndex === 0 ? cancelRef.current : closeRef.current;
    target?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      const key = event.key;
      if (
        key === 'Back' ||
        key === 'VK_BACK' ||
        key === 'GoBack' ||
        key === 'XF86Back' ||
        key === 'Escape' ||
        key === 'VK_EXIT' ||
        event.keyCode === 4 || // Android KEYCODE_BACK (Fire TV)
        event.keyCode === 27 // Escape legado
      ) {
        event.preventDefault();
        setOpen(false);
        return;
      }
      if (key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown') {
        event.preventDefault();
        setFocusIndex((i) => (i === 0 ? 1 : 0));
        return;
      }
      if (key === 'Enter' || key === 'NumpadEnter' || key === 'XF86OK' || key === 'Accept') {
        event.preventDefault();
        if (focusIndex === 0) setOpen(false);
        else closeApp();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, focusIndex]);

  if (!open) return null;

  return (
    <div className="tv-exit-confirm">
      <div className="tv-exit-confirm-card" role="dialog" aria-modal="true" aria-label={t('tv.exitConfirm.title')}>
        <KaraokrLogo alt="Karaokr" className="tv-exit-confirm-logo" />
        <h2 className="tv-exit-confirm-title">{t('tv.exitConfirm.title')}</h2>
        <p className="tv-exit-confirm-sub">{t('tv.exitConfirm.subtitle')}</p>
        <div className="tv-exit-confirm-actions">
          <button
            ref={cancelRef}
            type="button"
            className={`tv-exit-confirm-btn tv-exit-confirm-btn--primary${focusIndex === 0 ? ' is-focused' : ''}`}
            onMouseEnter={() => setFocusIndex(0)}
            onFocus={() => setFocusIndex(0)}
            onClick={() => setOpen(false)}
          >
            {t('tv.exitConfirm.cancel')}
          </button>
          <button
            ref={closeRef}
            type="button"
            className={`tv-exit-confirm-btn tv-exit-confirm-btn--danger${focusIndex === 1 ? ' is-focused' : ''}`}
            onMouseEnter={() => setFocusIndex(1)}
            onFocus={() => setFocusIndex(1)}
            onClick={closeApp}
          >
            {t('tv.exitConfirm.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
